// Command palette — "Go to any page in any app". Opened with Ctrl+K /
// ⌘K or "/", or the Search button in the top bar.
//
// Accessibility contract (WAI-ARIA combobox with a listbox popup):
//   - role="dialog" + aria-modal; focus moves to the input on open and
//     returns to whatever had it on close.
//   - The input is the only tab stop (Tab is held inside the dialog);
//     ↑/↓ move the active option, Enter opens it, Esc closes.
//   - The active option is announced through aria-activedescendant, and
//     a polite live region reads the result count as the user types.
//   - No animation, so prefers-reduced-motion has nothing to suppress.
//
// The panel is white with a thin border, soft shadow and faint ring —
// the shell's pop-out style (app switcher, slide-out preview) — and has
// an explicit dark-mode pair for every colour, so it follows the theme.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { CornerDownLeft, Search } from 'lucide-react';
import { contextLine, rankPaletteEntries, type PaletteEntry } from '../../lib/commandPalette';

interface Props {
  open: boolean;
  onClose: () => void;
  entries: PaletteEntry[];
  recents: string[];
  /** Apps in the person's focus, ranked first. null = no preference. */
  focusAppKeys?: ReadonlySet<string> | null;
  onNavigate: (to: string) => void;
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex min-w-[1.5rem] items-center justify-center rounded border border-slate-300 bg-slate-50 px-1 font-mono text-[11px] font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
      {children}
    </kbd>
  );
}

export function CommandPalette({ open, onClose, entries, recents, focusAppKeys = null, onNavigate }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const listId = useId();

  const results = useMemo(
    () => rankPaletteEntries(entries, query, recents, undefined, focusAppKeys),
    [entries, query, recents, focusAppKeys],
  );
  const recentSet = useMemo(() => new Set(recents), [recents]);

  // Open: remember who had focus, reset, focus the input.
  // Close: hand focus back. Both on the open transition only.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
      setQuery('');
      setActive(0);
      const raf = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    const back = restoreFocusRef.current;
    restoreFocusRef.current = null;
    if (back && typeof back.focus === 'function') back.focus();
    return undefined;
  }, [open]);

  // Keep the active option in range and in view.
  useEffect(() => {
    if (active >= results.length) setActive(results.length > 0 ? results.length - 1 : 0);
  }, [results.length, active]);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, results]);

  if (!open) return null;

  const choose = (e: PaletteEntry | undefined) => {
    if (!e) return;
    onClose();
    onNavigate(e.to);
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLElement>) => {
    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        if (results.length > 0) setActive((i) => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        ev.preventDefault();
        if (results.length > 0) setActive((i) => (i - 1 + results.length) % results.length);
        break;
      case 'Enter':
        ev.preventDefault();
        choose(results[active]);
        break;
      case 'Escape':
        ev.preventDefault();
        onClose();
        break;
      case 'Tab':
        // The input is the dialog's only tab stop; keep focus inside.
        ev.preventDefault();
        inputRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const activeEntry = results[active];
  const showingRecents = query.trim() === '';

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]" onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-slate-900/40" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex w-full max-w-xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-2xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        style={{ opacity: 1 }}
      >
        <h2 id={titleId} className="sr-only">
          Go to a page
        </h2>
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-700">
          <Search className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls={listId}
            aria-activedescendant={activeEntry ? activeEntry.id : undefined}
            aria-autocomplete="list"
            aria-label="Search pages across every app"
            placeholder="Search pages across every app…"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            className="h-12 min-w-0 flex-1 bg-transparent text-[15px] text-slate-900 outline-none placeholder:text-slate-500 dark:text-slate-100 dark:placeholder:text-slate-400"
          />
          <Kbd>Esc</Kbd>
        </div>

        {showingRecents && recents.length > 0 && results.length > 0 && (
          <p className="px-5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400" aria-hidden="true">
            Recent first
          </p>
        )}
        {results.length > 0 ? (
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={showingRecents ? 'Recent and suggested pages' : 'Matching pages'}
            className="max-h-[50vh] overflow-y-auto p-2"
          >
            {results.map((e, i) => {
              const isActive = i === active;
              return (
                <li
                  key={e.id}
                  id={e.id}
                  role="option"
                  aria-selected={isActive}
                  data-index={i}
                  onMouseMove={() => setActive(i)}
                  onClick={() => choose(e)}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 ${
                    isActive ? 'bg-indigo-600 text-white' : 'text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-2 truncate text-sm font-medium">
                      <span className="truncate">{e.label}</span>
                      {showingRecents && recentSet.has(e.to) && (
                        <span
                          className={`shrink-0 rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider ${
                            isActive ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300'
                          }`}
                        >
                          Recent
                        </span>
                      )}
                    </span>
                    <span className={`truncate text-xs ${isActive ? 'text-indigo-100' : 'text-slate-600 dark:text-slate-400'}`}>
                      {contextLine(e)}
                    </span>
                  </span>
                  {isActive && <CornerDownLeft className="h-4 w-4 shrink-0 text-white/80" aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="px-5 py-8 text-sm text-slate-700 dark:text-slate-300">
            No page matches “{query.trim()}”. Try a product name such as Surface or Resilience, or part of a page
            name such as “campaigns”.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-200 px-4 py-2 text-xs text-slate-700 dark:border-slate-700 dark:text-slate-300">
          <span className="inline-flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> move
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Enter</Kbd> open
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Esc</Kbd> close
          </span>
          <span className="ml-auto tabular-nums text-slate-600 dark:text-slate-400">
            {showingRecents ? `${entries.length} pages you can open` : `${results.length} ${results.length === 1 ? 'match' : 'matches'}`}
          </span>
        </div>
        <div className="sr-only" aria-live="polite">
          {showingRecents ? '' : results.length === 0 ? 'No matching pages' : `${results.length} matching ${results.length === 1 ? 'page' : 'pages'}`}
        </div>
      </div>
    </div>
  );
}
